// File: AccountingCategoriesManagement.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  FaPlus, FaEdit, FaTrash, FaTag, FaPalette, FaList, 
  FaArrowUp, FaArrowDown, FaMoneyBillWave, FaExchangeAlt 
} from 'react-icons/fa';
import { 
  getAccountingCategories, 
  createAccountingCategory, 
  updateAccountingCategory,
  deleteAccountingCategory,
  AccountingCategory 
} from '../../../firebase/accountingFirestoreService';
import { useUI } from '../../../components/common/UIMessageProvider';

const AccountingCategoriesManagement: React.FC = () => {
  const auth = useAuth();
  const canViewAccounting = auth.canPerformAction('accounting', 'view');
  const canAddAccounting = auth.canPerformAction('accounting', 'create');
  const canEditAccounting = auth.canPerformAction('accounting', 'edit');
  const canDeleteAccounting = auth.canPerformAction('accounting', 'delete');
  const [categories, setCategories] = useState<AccountingCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState<AccountingCategory | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<AccountingCategory>>({
    name: '',
    description: '',
    color: '#3b82f6',
    type: 'Expense'
  });
  const ui = useUI();

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const result = await getAccountingCategories();
      if (result.success) {
        setCategories(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name?.trim()) {
      ui.showMessage('Category name is required', { type: 'warning' });
      return;
    }

    try {
      if (editingCategory?.id) {
        // Update existing
        const result = await updateAccountingCategory(editingCategory.id, formData);
        if (result.success) {
          setCategories(prev => prev.map(c => 
            c.id === editingCategory.id ? { ...c, ...formData } : c
          ));
        } else {
          console.error('Failed to update category:', result.error);
          ui.showMessage('Failed to update category: ' + (result.error || 'Unknown error'), { type: 'error' });
        }
      } else {
        // Create new
        const result = await createAccountingCategory(formData as AccountingCategory);
        if (result.success) {
          fetchCategories(); // Refresh list
        } else {
          console.error('Failed to create category:', result.error);
          ui.showMessage('Failed to create category: ' + (result.error || 'Unknown error'), { type: 'error' });
        }
      }
      
      // Reset form
      setFormData({ name: '', description: '', color: '#3b82f6', type: 'Expense' });
      setEditingCategory(null);
      setShowForm(false);
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleEdit = (category: AccountingCategory) => {
    setEditingCategory(category);
    setFormData(category);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const ok = await ui.askConfirm({
      title: 'Delete Category',
      message: 'Are you sure you want to delete this category?',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!ok) return;
    try {
      const result = await deleteAccountingCategory(id);
      if (result.success) {
        setCategories(prev => prev.filter(c => c.id !== id));
        ui.showMessage('Category deleted', { type: 'success' });
      } else {
        ui.showMessage('Failed to delete category', { type: 'error' });
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      ui.showMessage('Failed to delete category', { type: 'error' });
    }
  };

  const colorOptions = [
    '#3b82f6', // Blue
    '#10b981', // Green
    '#f59e0b', // Yellow
    '#ef4444', // Red
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#06b6d4', // Cyan
    '#84cc16', // Lime
    '#f97316', // Orange
    '#6366f1', // Indigo
  ];

  const typeOptions = [
    { value: 'Expense', label: 'Expense', icon: FaArrowDown, color: 'text-red-500' },
    { value: 'Income', label: 'Income', icon: FaArrowUp, color: 'text-green-500' },
    { value: 'Transfer', label: 'Transfer', icon: FaExchangeAlt, color: 'text-blue-500' },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Accounting Categories</h1>
            <p className="text-sm text-gray-600">Manage financial categories</p>
          </div>
          {canAddAccounting && (
            <button
              onClick={() => {
                setEditingCategory(null);
                setFormData({ name: '', description: '', color: '#3b82f6', type: 'Expense' });
                setShowForm(true);
              }}
              className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
            >
              <FaPlus className="h-4 w-4" />
              Add
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600">Total</p>
                <p className="text-lg font-semibold text-gray-900">{categories.length}</p>
              </div>
              <FaList className="h-6 w-6 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600">Income</p>
                <p className="text-lg font-semibold text-gray-900">{categories.filter(c => c.type === 'Income').length}</p>
              </div>
              <FaArrowUp className="h-6 w-6 text-green-500" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600">Expense</p>
                <p className="text-lg font-semibold text-gray-900">{categories.filter(c => c.type === 'Expense').length}</p>
              </div>
              <FaArrowDown className="h-6 w-6 text-red-500" />
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="mb-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-base font-medium text-gray-900 mb-3">{editingCategory ? 'Edit Category' : 'Add Category'}</h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Office Supplies"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {typeOptions.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, type: type.value as any }))}
                          className={`flex items-center justify-center gap-2 px-2 py-1 rounded-md border text-sm transition-colors ${formData.type === type.value ? `${type.color} border-current bg-opacity-10` : 'border-gray-300 hover:bg-gray-50'}`}>
                          <Icon className="h-4 w-4" />
                          <span className="text-sm">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color *</label>
                <div className="flex flex-wrap gap-2 items-center">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color }))}
                      className={`w-8 h-8 rounded-md border-2 ${formData.color === color ? 'border-gray-900' : 'border-gray-300'}`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                  <div className="relative">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="w-8 h-8 rounded-md border border-gray-300 cursor-pointer"
                    />
                    <FaPalette className="absolute inset-0 m-auto h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t">
                <button type="button" onClick={() => { setShowForm(false); setEditingCategory(null); }} className="px-4 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm">Cancel</button>
                <button type="submit" className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-1.5 rounded-md text-sm"><FaPlus className="h-4 w-4" />{editingCategory ? 'Update' : 'Save'}</button>
              </div>
            </form>
          </div>
        )}

        {/* Categories List */}
        <div className="bg-white rounded-xl shadow-soft overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : categories.length === 0 ? (
              <div className="text-center py-12">
                <FaTag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No categories yet</h3>
                <p className="text-gray-600 mb-4">Create your first accounting category to get started</p>
                {canAddAccounting && (
                  <button
                    onClick={() => setShowForm(true)}
                    className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
                  >
                    <FaPlus className="h-4 w-4" />
                    Add First Category
                  </button>
                )}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Color</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {categories.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: category.color }}>
                            <FaTag className="h-3 w-3 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{category.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${category.type === 'Income' ? 'bg-green-100 text-green-800 border border-green-200' : category.type === 'Expense' ? 'bg-red-100 text-red-800 border border-red-200' : 'bg-blue-100 text-blue-800 border border-blue-200'}`}>
                          {category.type === 'Income' && <FaArrowUp className="h-3 w-3" />}
                          {category.type === 'Expense' && <FaArrowDown className="h-3 w-3" />}
                          {category.type === 'Transfer' && <FaExchangeAlt className="h-3 w-3" />}
                          {category.type}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: category.color }} />
                          <span className="text-sm text-gray-600 font-mono">{category.color}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">{category.description || '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {canEditAccounting && (
                            <button onClick={() => handleEdit(category)} className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50" title="Edit">
                              <FaEdit className="h-4 w-4" />
                            </button>
                          )}
                          {canDeleteAccounting && (
                            <button onClick={() => category.id && handleDelete(category.id)} className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50" title="Delete">
                              <FaTrash className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountingCategoriesManagement;